// The guided tour of Approvals, for anybody who has people to answer to them.
//
// Two things here are genuinely new rather than moved, and both are easy to
// get wrong on a first day. The page is one queue where there were two, so a
// manager who knew where leave lived has to be told it is a tab now; and
// recording an absence for somebody did not exist at all, so the difference
// between recording and requesting has to be said out loud, because the form
// looks like the request form and behaves nothing like it.
//
// Steps point at [data-tour] anchors on ApprovalsPage and, inside the Leave
// tab, on HolidaysPage. A step whose target is missing shows centred with no
// spotlight rather than crashing, which is the right fallback for a panel that
// is merely empty today. It is the wrong one for a tab somebody's role will
// never give them, so those steps carry `when` and are dropped before the tour
// starts: the count in the card is honest, and nobody spends a step of a short
// tour on something they cannot see.
//
// The conditions read the same facts the page uses to decide whether to draw
// each tab. A second copy of the role list here would be a second thing to
// forget the next time the roles change.

export const APPROVALS_TOUR_ID = 'approvals';

// Move the page onto the tab a step describes, so the spotlight has something
// to land on. Every step in a stretch carries the hook, not only the one that
// enters it, so stepping backwards leaves the page as it was going forwards.
const showTab = (label) => () => {
  const strip = document.querySelector('[data-tour="approval-tabs"]');
  const btn = [...(strip?.querySelectorAll('button') || [])].find(b => b.textContent.trim().startsWith(label));
  btn?.click();
};
const onLeave = showTab('Leave');
const onOvertime = showTab('Overtime');

export const approvalsTourSteps = [
  {
    title: 'Approvals',
    target: '[data-tour="approvals-header"]',
    body: [
      'Everything waiting on you to decide about your team, in one place. Leave and overtime used to be two separate screens, which meant knowing which one held the thing you were looking for; they are tabs of this page now, and the number on each tab is how many are actually waiting on you.',
      'Use Next, or the arrow keys, to walk through. Skip if you would rather explore: either way it will not open again by itself, and the ? beside the title replays it whenever you want.',
    ],
  },
  {
    title: 'A tab for each kind',
    target: '[data-tour="approval-tabs"]',
    body: [
      'You see the tabs that are yours. Leave is there if anybody reports to you, overtime if your role has been given it in Roles & Permissions, and Hiring only while a vacancy or an offer is actually sitting with you to decide. Somebody with none of them gets told that nothing is waiting rather than an empty page.',
      'A tab is the screen it always was, so nothing you already knew how to do has moved inside it.',
    ],
  },
  {
    title: 'Leave waiting on you',
    target: '[data-tour="leave-queue"]',
    when: (c) => c.maySeeLeave,
    before: onLeave,
    body: [
      'Holiday, sickness and everything else your team asks for. Pending is what needs you; Processed is the history, including anything you or HR has already dealt with. You can approve or reject anyone below you in the reporting line, not only your direct reports, so a manager being away does not leave their team stuck.',
      'Pending also lists any change to leave you have already approved, with the dates as approved beside the dates asked for. The approved leave stays booked until you decide, and rejecting the change keeps it as it was.',
      'By Person is the same information the other way round, which is the view to use when somebody asks how much they have left. HR and admin can edit a processed request from here as well as from Processed.',
    ],
  },
  {
    title: 'Recording an absence',
    target: '[data-tour="record-absence"]',
    when: (c) => c.maySeeLeave,
    before: onLeave,
    body: [
      'When somebody rings in ill they cannot file it themselves, and until they do the planner still has them at a desk. Record absence puts it on their record for them: pick the person, the type and the dates, and the days are worked out the same way the request form works them out.',
      'This is not a request and there is no approval step. It goes straight onto their record as approved, attributed to you, and the person is emailed a copy so nothing lands on their record without them hearing about it. If you get it wrong, Edit on the row is how it is put right.',
    ],
  },
  {
    title: 'Overtime and allowances',
    target: '[data-tour="approval-tabs"]',
    when: (c) => c.maySeeOvertime,
    before: onOvertime,
    body: [
      'Overtime, TOIL, standby, call-out and the desk and remote-working allowances, all claimed by your team and all decided here. Team Approvals is what is waiting on you; the same reporting-line rule applies, so you can decide for anybody beneath you.',
      'No rate is shown and none is asked of you. What a claim costs is a finance question and lives on the finance Overtime page; your decision is whether the time was worked.',
    ],
  },
  {
    title: 'What happens next',
    when: (c) => c.maySeeLeave || c.maySeeOvertime,
    body: [
      'Whoever asked is emailed your decision, and an approved claim or a booked holiday updates their balance straight away. Nothing else needs doing.',
      'These decisions also appear in My Actions, so if you work from that queue you will not miss one by not coming here. This page is the fuller view: the history, the by-person breakdown and the reports.',
    ],
  },
];
